import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import WebModal from './WebModal';
import { webColors, webBorderRadius } from '../../constants/webStyles';
import { COUNTRIES } from '../../constants/countries';

const WebJobCreationProgress = ({ visible, progress, onClose }) => {
  const getCountryInfo = (countryCode) => {
    return COUNTRIES[countryCode] || { name: countryCode, flag: '🏳️' };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={18} color="#999" />;
      case 'in_progress':
        return <ActivityIndicator size="small" color={webColors.info} />;
      case 'success':
        return <CheckCircle size={18} color={webColors.success} />;
      case 'failed':
        return <XCircle size={18} color={webColors.error} />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Waiting...';
      case 'in_progress': return 'Creating job...';
      case 'success': return 'Job created';
      case 'failed': return 'Failed';
      default: return '';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#999';
      case 'in_progress': return webColors.info;
      case 'success': return webColors.success;
      case 'failed': return webColors.error;
      default: return '#666';
    }
  };

  return (
    <WebModal
      visible={visible}
      onClose={onClose}
      title="Creating Jobs"
      subtitle="Creating your job posting for multiple countries"
      maxWidth={440}
    >
      <View style={styles.progressSummary}>
        <Text style={styles.progressText}>
          {progress?.completed || 0} of {progress?.total || 0} completed
        </Text>
      </View>

      <View style={styles.countryList}>
        {progress?.countries?.map((country, index) => {
          const countryInfo = getCountryInfo(country.code);
          return (
            <View
              key={country.code}
              style={[
                styles.countryRow,
                index < (progress?.countries?.length || 0) - 1 && styles.countryRowBorder,
              ]}
            >
              <Text style={styles.countryFlag}>{countryInfo.flag}</Text>
              <View style={styles.countryInfo}>
                <Text style={styles.countryName}>{countryInfo.name}</Text>
                <Text style={[styles.countryStatus, { color: getStatusColor(country.status) }]}>
                  {getStatusText(country.status)}
                  {country.error ? ` - ${country.error}` : ''}
                </Text>
              </View>
              {getStatusIcon(country.status)}
            </View>
          );
        })}
      </View>

      {progress?.completed < progress?.total && (
        <Text style={styles.loadingText}>
          Please wait while we create your job postings...
        </Text>
      )}

      {progress?.completed >= progress?.total && (
        <TouchableOpacity onPress={onClose} style={styles.closeButton}>
          <Text style={styles.closeButtonText}>Close</Text>
        </TouchableOpacity>
      )}
    </WebModal>
  );
};

const styles = StyleSheet.create({
  progressSummary: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
    padding: 12,
    marginBottom: 16,
  },
  progressText: {
    fontSize: 14, color: webColors.textPrimary, textAlign: 'center', fontWeight: '500',
  },
  countryList: { marginBottom: 8 },
  countryRow: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 12, gap: 12,
  },
  countryRowBorder: {
    borderBottomWidth: 1, borderBottomColor: webColors.borderLight,
  },
  countryFlag: { fontSize: 20 },
  countryInfo: { flex: 1 },
  countryName: { fontSize: 15, color: webColors.textPrimary, fontWeight: '500' },
  countryStatus: { fontSize: 12, marginTop: 2 },
  loadingText: {
    fontSize: 12, color: webColors.textMuted, textAlign: 'center', marginTop: 8, fontStyle: 'italic',
  },
  closeButton: {
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 16,
  },
  closeButtonText: {
    fontSize: 15,
    color: webColors.primary,
    fontWeight: '600',
  },
});

export default WebJobCreationProgress;
